import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { AppProvider } from "@/context/AppContext";
import { AppShell } from "@/components/layout/AppShell";
import { Toaster } from "@/components/ui/sonner";
import "@/App.css";

import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Home from "@/pages/Home";
import Dashboard from "@/pages/Dashboard";
import Campaigns from "@/pages/Campaigns";
import CampaignDetail from "@/pages/CampaignDetail";
import Calendar from "@/pages/Calendar";
import Launches from "@/pages/Launches";
import Photoshoots from "@/pages/Photoshoots";
import Budgets from "@/pages/Budgets";
import AIContentStudio from "@/pages/AIContentStudio";
import Social from "@/pages/Social";
import AIInsights from "@/pages/AIInsights";
import Influencers from "@/pages/Influencers";
import Tasks from "@/pages/Tasks";
import Approvals from "@/pages/Approvals";
import Assets from "@/pages/Assets";
import Analytics from "@/pages/Analytics";
import Notifications from "@/pages/Notifications";
import Team from "@/pages/Team";
import Settings from "@/pages/Settings";
import Inspiration from "@/pages/Inspiration";
import CollaboratorCRM from "@/pages/CollaboratorCRM";
import GenericModule from "@/pages/GenericModule";

function Protected({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-brand-violet border-t-transparent" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  return <AppShell>{children}</AppShell>;
}

function Shell() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/" element={<Protected><Home /></Protected>} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/campaigns" element={<Protected><Campaigns /></Protected>} />
      <Route path="/campaigns/:id" element={<Protected><CampaignDetail /></Protected>} />
      <Route path="/calendar" element={<Protected><Calendar /></Protected>} />
      <Route path="/launches" element={<Protected><Launches /></Protected>} />
      <Route path="/photoshoots" element={<Protected><Photoshoots /></Protected>} />
      <Route path="/budgets" element={<Protected><Budgets /></Protected>} />
      <Route path="/studio" element={<Protected><AIContentStudio /></Protected>} />
      <Route path="/social" element={<Protected><Social /></Protected>} />
      <Route path="/assets" element={<Protected><Assets /></Protected>} />
      <Route path="/inspiration" element={<Protected><Inspiration /></Protected>} />
      <Route path="/seo" element={<Protected><GenericModule moduleKey="seo" /></Protected>} />
      <Route path="/ads" element={<Protected><GenericModule moduleKey="ads" /></Protected>} />
      <Route path="/messaging" element={<Protected><GenericModule moduleKey="messaging" /></Protected>} />
      <Route path="/promotions" element={<Protected><GenericModule moduleKey="promotions" /></Protected>} />
      <Route path="/affiliates" element={<Protected><GenericModule moduleKey="affiliates" /></Protected>} />
      <Route path="/influencers" element={<Protected><Influencers /></Protected>} />
      <Route path="/segments" element={<Protected><GenericModule moduleKey="segments" /></Protected>} />
      <Route path="/crm" element={<Protected><CollaboratorCRM /></Protected>} />
      <Route path="/pr" element={<Protected><GenericModule moduleKey="pr" /></Protected>} />
      <Route path="/events" element={<Protected><GenericModule moduleKey="events" /></Protected>} />
      <Route path="/analytics" element={<Protected><Analytics /></Protected>} />
      <Route path="/insights" element={<Protected><AIInsights /></Protected>} />
      <Route path="/competitors" element={<Protected><GenericModule moduleKey="competitors" /></Protected>} />
      <Route path="/reports" element={<Protected><GenericModule moduleKey="reports" /></Protected>} />
      <Route path="/tasks" element={<Protected><Tasks /></Protected>} />
      <Route path="/approvals" element={<Protected><Approvals /></Protected>} />
      <Route path="/notifications" element={<Protected><Notifications /></Protected>} />
      <Route path="/team" element={<Protected><Team /></Protected>} />
      <Route path="/settings" element={<Protected><Settings /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppProvider>
          <Shell />
          <Toaster position="top-right" richColors theme="dark" />
        </AppProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
